import { formatPriceLak } from "@/shared/lib/format-price";
import { format } from "date-fns";
import React from "react";
import logo from "../../../../../assets/favicon_io/android-chrome-192x192.png";
import {
  buildPrintCarrierDisplay,
  formatPrintBillLocationLine,
  formatPrintVariantOptions,
  type OrderPrintData,
} from "../../utils/print-types";
import { formatOrderPaymentMethodLabel } from "../../utils/order-retail-display";

interface InvoicePrintViewProps {
  order: OrderPrintData;
}

// Brand configuration - can be moved to settings later
const BRAND_NAME = "Freedom";
// Use absolute path for logo in print window (favicon_io)
const BRAND_LOGO = logo;

export function InvoicePrintView({ order }: InvoicePrintViewProps) {
  const storePhone = order.contactPhone?.trim();
  const items = order.items.map((item, idx) => ({
    index: idx + 1,
    name: item.productName ?? "-",
    variant: formatPrintVariantOptions(item.resolvedVariantOptions),
    quantity: item.quantity,
    unitPrice: Number(item.variantPrice || item.price),
    total: item.total,
  }));

  const subtotal = order.items.reduce((sum, item) => sum + item.total, 0);
  const shipping = order.shippingFee ?? 0;
  const discount = order.discountTotal ?? 0;
  const tax = 0; // TODO: Add tax calculation if needed
  const total = order.grandTotal;

  const billingAddress = (() => {
    const loc = formatPrintBillLocationLine(order.shipping);
    return loc || "-";
  })();

  const carrierLine = buildPrintCarrierDisplay(order.shipping);

  return (
    <div className="print-container invoice">
      {/* Header Section */}
      <div className="invoice-header">
        <div className="invoice-header-left">
          <div className="brand-logo">
            <img
              src={BRAND_LOGO}
              alt={BRAND_NAME}
              className="logo-image"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          </div>
          <div className="company-info">
            <div className="store-name">{BRAND_NAME}</div>
            <div className="company-details">
              <div>Vientiane, Laos</div>
              {storePhone ? <div>Call: {storePhone}</div> : null}
            </div>
          </div>
        </div>
        <div className="invoice-header-right">
          <div className="document-type">INVOICE</div>
          <div className="invoice-meta">
            <div className="meta-row">
              <span className="meta-label">Invoice No:</span>
              <span className="meta-value">{order.orderNumber}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">Order No:</span>
              <span className="meta-value">{order.orderNumber}</span>
            </div>
            <div className="meta-row">
              <span className="meta-label">Date:</span>
              <span className="meta-value">
                {format(new Date(order.createdAt), "dd MMM yyyy")}
              </span>
            </div>
            <div className="meta-row">
              <span className="meta-label">Time:</span>
              <span className="meta-value">
                {format(new Date(order.createdAt), "HH:mm")}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="invoice-divider" />

      {/* Billing and Customer Info */}
      <div className="invoice-info-grid">
        <div className="info-block">
          <div className="info-block-title">Bill To:</div>
          <div
            className="info-block-content"
            style={{ whiteSpace: "pre-line" }}
          >
            {order.customer?.name ?? "-"}
            {order.customer?.email ? `\n${order.customer.email}` : ""}
            {order.customer?.phoneNumber
              ? `\n${order.customer.phoneNumber}`
              : ""}
            {billingAddress !== "-" ? `\n\n${billingAddress}` : ""}
            {carrierLine ? `\n\nCarrier: ${carrierLine}` : ""}
          </div>
        </div>
        <div className="info-block">
          <div className="info-block-title">Payment Information:</div>
          <div className="info-block-content">
            <div className="info-item">
              <span className="info-label">Method:</span>
              <span className="info-value">
                {formatOrderPaymentMethodLabel(
                  order.paymentMethod,
                  order.channel,
                )}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Status:</span>
              <span className="info-value">{order.paymentStatus}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="invoice-divider" />

      {/* Items Table */}
      <div className="invoice-items-section">
        <div className="section-title">Items</div>
        <table className="invoice-items-table">
          <thead>
            <tr>
              <th className="col-item">Item</th>
              <th className="col-qty">Qty</th>
              <th className="col-price">Unit Price</th>
              <th className="col-total">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <React.Fragment key={item.index}>
                <tr className="item-row">
                  <td className="col-item">
                    <div className="item-name">{item.name}</div>
                    {item.variant && (
                      <div className="item-variant">{item.variant}</div>
                    )}
                  </td>
                  <td className="col-qty">{item.quantity}</td>
                  <td className="col-price">
                    {formatPriceLak(item.unitPrice)}
                  </td>
                  <td className="col-total">{formatPriceLak(item.total)}</td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals Section */}
      <div className="invoice-totals">
        <div className="totals-table">
          <div className="total-row">
            <span className="total-label">Subtotal:</span>
            <span className="total-value">{formatPriceLak(subtotal)}</span>
          </div>
          {shipping > 0 && (
            <div className="total-row">
              <span className="total-label">Shipping:</span>
              <span className="total-value">{formatPriceLak(shipping)}</span>
            </div>
          )}
          {discount > 0 && (
            <div className="total-row">
              <span className="total-label">Discount:</span>
              <span className="total-value">-{formatPriceLak(discount)}</span>
            </div>
          )}
          {tax > 0 && (
            <div className="total-row">
              <span className="total-label">Tax:</span>
              <span className="total-value">{formatPriceLak(tax)}</span>
            </div>
          )}
          <div className="total-row total-row-final">
            <span className="total-label">Total Amount:</span>
            <span className="total-value">{formatPriceLak(total)}</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="invoice-footer">
        <div className="footer-text">Thank you for your business!</div>
        <div className="footer-note">
          This is a computer-generated invoice. No signature required.
        </div>
      </div>
    </div>
  );
}
